import './EditableSelect.scss';
import Select, { ActionMeta, OnChangeValue, Options } from 'react-select';
import Creatable from 'react-select/creatable';
import { SelectOption } from '@luna-protocol/core/src/types';
import { FieldHelperProps, useField } from 'formik';
import { FocusEventHandler } from 'react';
import { USDollar } from '@luna-protocol/core/src/utils/constants/format.ts';

interface EditableSelectProps {
  label?: string;
  placeholder?: string;
  value: string | SelectOption | undefined;
  onChange?: (newValue: string | SelectOption | undefined) => void;
  onFormikValueChange?: (newValue: string | SelectOption | undefined) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  name?: string;
  disabled?: boolean;
  error?: string;
  disclaimer?: string;
  required?: boolean;
  options: string[] | SelectOption[] | undefined;
  className?: string;
  formik?: boolean;
  isClearable?: boolean;
  allowCreate?: boolean;
  editable?: boolean;
  viewOnly?: boolean;
  testId?: string;
}

const EditableSelect = ({
  label,
  placeholder,
  value,
  onChange,
  name,
  disabled = false,
  formik = true,
  required,
  options,
  onBlur,
  className,
  isClearable = false,
  allowCreate = false,
  editable = false,
  viewOnly = false,
  testId,
  onFormikValueChange,
}: EditableSelectProps) => {
  let formikHelpers: FieldHelperProps<unknown>;
  if (formik) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [, , helpers] = useField(name || '');
    formikHelpers = helpers;
  }

  function makeOptions(options: string[] | SelectOption[] | undefined): Options<SelectOption> {
    if (options === undefined) {
      return [];
    }
    if (typeof options[0] === 'object' && 'value' in options[0] && 'label' in options[0]) {
      return options as SelectOption[];
    }

    return options.map(o => ({ value: o, label: o })) as SelectOption[];
  }

  function makeValue(value: string | SelectOption | undefined): SelectOption | undefined {
    if (value === undefined) {
      return undefined;
    }
    if (typeof value === 'string') {
      return { value: value, label: value };
    }

    return value as SelectOption;
  }

  const handleInputChange = (e: OnChangeValue<SelectOption, false>, a: ActionMeta<SelectOption>) => {
  if (!onChange) {
    return;
  }
  if (e === undefined || e?.value === '') {
    onChange('');
    if (formik) {
      formikHelpers.setValue('');
      if (onFormikValueChange) {
        onFormikValueChange('');
      }
    }
    return;
  }

  switch (a.action) {
    case 'clear':
      if (formik) {
        formikHelpers.setValue('');
        if (onFormikValueChange) {
          onFormikValueChange('');
        }
      }
      onChange('');
      break;
    case 'select-option':
    case 'create-option':
      if (formik) {
        formikHelpers.setValue(e, true);
        if (onFormikValueChange) {
          onFormikValueChange(e as unknown as SelectOption);
        }
      }
      onChange((e as unknown as SelectOption).value);
      break;
  }
};

  const isPriceOrInvoice = label?.toLowerCase().includes('price') || label?.toLowerCase().includes('invoice') || label?.toLowerCase().includes('nada') ;
  const getStringValue = (value: string | SelectOption | undefined) =>
    typeof value === 'string' ? value : value && typeof value === 'object' ? value.value : '';

  const displayValue = getStringValue(value);

  return (
    <div className={`select--input--container--editable ${className ? className : ''}`}>
      <label htmlFor={name} className="label">
        {label}
      </label>
      {!editable ? (
        <p className={viewOnly ? 'value-right' : 'value-right editable'}>
          {isPriceOrInvoice ? USDollar.format(Number(displayValue)) : displayValue}
        </p>
      ) : (
        <>
          {allowCreate ? (
            <Creatable
              isClearable={isClearable}
              classNames={{
                control: () => 'select-input',
              }}
              name={name}
              id={name}
              controlShouldRenderValue
              onBlur={onBlur}
              onChange={handleInputChange}
              value={makeValue(value)}
              isDisabled={disabled}
              required={required}
              placeholder={placeholder}
              data-testid={`${testId || name}-select-input`}
              options={makeOptions(options)}
            />
          ) : (
            <Select
              isClearable={isClearable}
              classNames={{
                control: () => 'select-input',
              }}
              name={name}
              id={name}
              controlShouldRenderValue
              onBlur={onBlur}
              onChange={handleInputChange}
              value={makeValue(value)}
              isDisabled={disabled}
              required={required}
              placeholder={placeholder}
              data-testid={`${testId || name}-select-input`}
              options={makeOptions(options)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EditableSelect;
